// src/components/ModalProfesionalDetail.js

import React, { useRef, useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchProfessionalById } from '../api/queries';
import { useNavigate } from 'react-router-dom';
import ActionButton from '../components/ActionButton';
import './ModalProfesionalDetail.css';
import { IoPeopleOutline } from "react-icons/io5";
import { PiPhone } from "react-icons/pi";
import { FaWhatsapp } from "react-icons/fa";
import { HiOutlineMail } from "react-icons/hi";
import { TfiAlarmClock } from "react-icons/tfi";

export default function ModalProfesionalDetail({ professionalId }) {
  // ➤ React Query: caché de detalles de profesional
  const {
    data: profData,
    isLoading,
    isError,
    error
  } = useQuery({
    queryKey: ['professional', professionalId],
    queryFn: () => fetchProfessionalById(professionalId),
    staleTime: 5 * 60_000,   // 5 minutos en caché antes de refetch
    cacheTime: 30 * 60_000   // 30 minutos en memoria
  });

  // Para navegar atrás y cerrar modal
  const navigate = useNavigate();

  // ---------------------------
  // Mecanismo para el contenedor de botones sticky.
  // ---------------------------
  const buttonContainerRef = useRef(null);
  const [isStuck, setIsStuck] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      if (buttonContainerRef.current) {
        const rect = buttonContainerRef.current.getBoundingClientRect();
        if (rect.top <= -20) {
          setIsStuck(true);
        } else {
          setIsStuck(false);
        }
      }
    };
    window.addEventListener("scroll", handleScroll);
    handleScroll(); // Ejecutar inicialmente
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleButtonClick = () => {
    if (!isStuck && buttonContainerRef.current) {
      buttonContainerRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  // Función para cerrar el modal: utiliza navigate(-1)
  const handleClose = () => {
    navigate(-1);
  };

  // Si aún no se han cargado los datos, mostramos un mensaje de carga
  if (isLoading) {
    return (
      <div className="modalProfOverlay" onClick={handleClose}>
        <div className="modalProfCard" onClick={e => e.stopPropagation()}>
          <p>Cargando profesional...</p>
        </div>
      </div>
    );
  }
  if (isError) {
    console.error('[ModalProfesionalDetail] Error fetching professional:', error);
    return (
      <div className="modalProfOverlay" onClick={handleClose}>
        <div className="modalProfCard" onClick={e => e.stopPropagation()}>
          <p>Error al cargar profesional.</p>
        </div>
      </div>
    );
  }

  // Renderizado del contenido del modal usando los datos en profData
  return (
    <div className="modalProfOverlay" onClick={handleClose}>
      <div className="modalProfCard" onClick={e => e.stopPropagation()}>
        {profData.cabecera && (
          <div className="modalProfHeaderImage">
            <img src={profData.cabecera} alt="Cabecera" />
          </div>
        )}

        <div className="modalProfInfoTop">
          {profData.tipo && (
            <p className="icons-info">
              <IoPeopleOutline className="icon" />
              <span className="info-text">{profData.tipo}</span>
            </p>
          )}
          <div className="contact-info">
            {profData.telefono && (
              <div className="icons-info">
                <PiPhone className="icon" />
                <span className="info-text">
                  <a href={`tel:${profData.telefono}`}>{profData.telefono}</a>
                </span>
              </div>
            )}
            {profData.whatsapp && (
              <div className="icons-info">
                <FaWhatsapp className="icon" />
                <span className="info-text">
                  <a href={`https://wa.me/34${profData.whatsapp}`} target="_blank" rel="noopener noreferrer">
                    {profData.whatsapp}
                  </a>
                </span>
              </div>
            )}
          </div>
          {profData.email && (
            <p className="icons-info">
              <HiOutlineMail className="icon" />
              <span className="info-text">
                <a href={`mailto:${profData.email}`}>{profData.email}</a>
              </span>
            </p>
          )}
          {profData.horario && (
          <p className="icons-info">
            <TfiAlarmClock className="icon" />
            <span className="info-text">{profData.horario}</span>
          </p>
        )}
        </div>

        <div ref={buttonContainerRef} className={`modalProfButtonContainer ${isStuck ? 'stuck' : ''}`}>
          <div className="redesSociales">
            {profData.enlace && (
              <div className="redesSociales-text">
                <a href={profData.enlace} target="_blank" rel="noopener noreferrer" className="web-link">
                  Visita nuestra web
                </a>
              </div>
            )}
            <div className="redesSociales-icons">
              {profData.facebook && (
                <a href={profData.facebook} target="_blank" rel="noopener noreferrer">
                  <img src="/Facebook.png" alt="Facebook" className="social-icon" />
                </a>
              )}
              {profData.instagram && (
                <a href={profData.instagram} target="_blank" rel="noopener noreferrer">
                  <img src="/Instagram.png" alt="Instagram" className="social-icon" />
                </a>
              )}
              {profData.twitter && (
                <a href={profData.twitter} target="_blank" rel="noopener noreferrer">
                  <img src="/Twitter.png" alt="Twitter" className="social-icon" />
                </a>
              )}
              {profData.youtube && (
                <a href={profData.youtube} target="_blank" rel="noopener noreferrer">
                  <img src="/Youtube.png" alt="Youtube" className="social-icon" />
                </a>
              )}
            </div>
          </div>
          <div className="prof-buttons-row">
            <ActionButton label="Acoge" url={profData.acoge} onClick={handleButtonClick} />
            <ActionButton label="Apadrina" url={profData.apadrina} onClick={handleButtonClick} />
            <ActionButton label="Voluntariado" url={profData.voluntariado} onClick={handleButtonClick} />
            <ActionButton label="Dona" url={profData.donaciones} onClick={handleButtonClick} />
            <ActionButton label="Socios" url={profData.socio} onClick={handleButtonClick} />
          </div>
        </div>

        <div className="modalProfDetails">
          {profData.aboutus && <p>{profData.aboutus}</p>}
        </div>
      </div>
    </div>
  );
}
